import { useState } from 'react';
import styled from '@emotion/styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import type { SegmentInsight } from '../api/client';
import { SEGMENT_COLORS } from '../constants/segmentColors';

// Trinity color palette - blue and green only
const TRINITY_COLORS = {
  confirming: SEGMENT_COLORS[3],  // '#8BC34A' - green
  enriching: SEGMENT_COLORS[0],   // '#1976D2' - blue
  tension: SEGMENT_COLORS[0],     // '#1976D2' - blue
  barrier: SEGMENT_COLORS[0],     // '#1976D2' - blue
  driver: SEGMENT_COLORS[3],      // '#8BC34A' - green
  neutral: SEGMENT_COLORS[0],     // '#1976D2' - blue
  text: '#323232',                // dark text for readability
  subtleText: SEGMENT_COLORS[0],  // '#1976D2' - blue for subtle text
  border: '#B3E0F5',              // light blue border
};

interface QualQuantOverlayProps {
  insight: SegmentInsight;
  segmentName?: string;
  personaLabel?: (personaId: string) => string;
  isPersonaBased?: boolean;
  defaultOpen?: boolean;
}

/**
 * QualQuantOverlay — Market Research × Claims Intelligence overlay.
 * Displays confirming signals, enriching insights, and tensions between
 * qualitative interview data and quantitative claims data.
 */
export default function QualQuantOverlay({
  insight,
  personaLabel,
  isPersonaBased,
  defaultOpen = false,
}: QualQuantOverlayProps): React.JSX.Element | null {
  const [tensionExpanded, setTensionExpanded] = useState(false);
  const [enrichExpanded, setEnrichExpanded] = useState(false);
  const [matchDetailOpen, setMatchDetailOpen] = useState(false);
  const [barrierQuotesOpen, setBarrierQuotesOpen] = useState<Record<number, boolean>>({});
  const [driverQuotesOpen, setDriverQuotesOpen] = useState<Record<number, boolean>>({});

  if (!insight) return null;

  const pm = insight.persona_match;
  const confirming = insight.confirming || [];
  const enriching = insight.enriching || [];
  const tensions = insight.tensions || [];
  const conv = insight.source_convergence || {};
  const hasContent = confirming.length > 0 || enriching.length > 0 || tensions.length > 0 || pm;

  const _personaLabel = personaLabel || ((id: string) => id);

  // No coverage case
  if (!hasContent) {
    if (insight.no_coverage) {
      return (
        <Root>
          <NoCoverageCard>
            <NoCoverageHeader>
              <Dot color={TRINITY_COLORS.neutral} />
              <NoCoverageTitle>No Market Research Coverage</NoCoverageTitle>
              <InfoIconStyled fontSize="small" />
            </NoCoverageHeader>
            <NoCoverageText>
              {insight.research_recommendation || 'No matching interview themes for this segment.'}
            </NoCoverageText>
          </NoCoverageCard>
        </Root>
      );
    }
    return null;
  }

  return (
    <Root>
      {/* Content - no header since wrapped by section */}
      {(
        <Content>
          {/* Mode framing + persona match detail */}
          {(insight.mode_framing || pm) && (
            <ModeFramingCard>
              {insight.mode_framing && <ModeText>{insight.mode_framing}</ModeText>}
              {pm && (
                <MatchDetailToggle onClick={() => setMatchDetailOpen((o) => !o)}>
                  {matchDetailOpen ? '▲ Hide match details' : '▼ Show match details'}
                </MatchDetailToggle>
              )}
              {matchDetailOpen && pm && (
                <MatchDetail>
                  <MatchDescription>
                    {isPersonaBased
                      ? 'Segment formed around this persona — HCPs pre-assigned via fingerprint similarity.'
                      : "Fingerprint similarity — how closely this segment's average claims profile matches each persona's expected pattern."}
                  </MatchDescription>
                </MatchDetail>
              )}
            </ModeFramingCard>
          )}

          {/* Confirming signals — green */}
          {confirming.length > 0 && (
            <ConfirmingSection>
              <SectionHeader>
                <Dot color={TRINITY_COLORS.confirming} />
                <SectionTitle color={TRINITY_COLORS.confirming}>Confirming</SectionTitle>
                <SectionSubtitle>
                  {confirming.length} signal{confirming.length > 1 ? 's' : ''} where claims and interviews agree
                </SectionSubtitle>
              </SectionHeader>
              <SignalList>
                {confirming.slice(0, 4).map((c, i) => (
                  <SignalItem key={i}>
                    <SignalText>{c.signal}</SignalText>
                    <EvidenceGrid>
                      <EvidenceItem>
                        <EvidenceLabel>Claims: </EvidenceLabel>
                        {c.claims_evidence}
                      </EvidenceItem>
                      <EvidenceItem>
                        <EvidenceLabel>Interviews: </EvidenceLabel>
                        {c.qual_evidence}
                      </EvidenceItem>
                    </EvidenceGrid>
                    {(c.obs_brand_id || c.obs_evidence_basis || c.obs_content_topic || c.obs_intensity) && (
                      <BadgeRow>
                        {c.obs_brand_id && <Badge color="purple">{c.obs_brand_id}</Badge>}
                        {c.obs_evidence_basis && (
                          <Badge color="indigo">{c.obs_evidence_basis.replace(/_/g, ' ')}</Badge>
                        )}
                        {c.obs_content_topic && (
                          <Badge color="blue">{c.obs_content_topic.replace(/_/g, ' ')}</Badge>
                        )}
                        {c.obs_intensity && (
                          <IntensityBadge intensity={c.obs_intensity}>{c.obs_intensity} intensity</IntensityBadge>
                        )}
                      </BadgeRow>
                    )}
                    {c.quote?.text && (
                      <Quote color={TRINITY_COLORS.confirming}>
                        "{c.quote.text.slice(0, 120)}
                        {c.quote.text.length > 120 ? '...' : ''}"
                      </Quote>
                    )}
                    {c.research_note && <ResearchNote>{c.research_note}</ResearchNote>}
                  </SignalItem>
                ))}
              </SignalList>
            </ConfirmingSection>
          )}

          {/* Enriching insights — blue */}
          {enriching.length > 0 && (
            <EnrichingSection>
              <SectionHeaderWithButton>
                <SectionHeaderLeft>
                  <Dot color={TRINITY_COLORS.enriching} />
                  <SectionTitle color={TRINITY_COLORS.enriching}>Enriching</SectionTitle>
                  <SectionSubtitle>Interviews explain the WHY behind claims</SectionSubtitle>
                </SectionHeaderLeft>
                {enriching.length > 3 && (
                  <ExpandButton onClick={() => setEnrichExpanded((e) => !e)}>
                    {enrichExpanded ? '▲ Less' : `▼ All ${enriching.length}`}
                  </ExpandButton>
                )}
              </SectionHeaderWithButton>
              <InsightList>
                {(enrichExpanded ? enriching : enriching.slice(0, 3)).map((e, i) => (
                  <InsightItem key={i}>
                    <InsightText>{e.insight}</InsightText>
                    {(e.obs_evidence_basis || e.obs_brand_id) && (
                      <BadgeRow>
                        {e.obs_brand_id && <Badge color="purple">{e.obs_brand_id}</Badge>}
                        {e.obs_evidence_basis && <Badge color="indigo">{e.obs_evidence_basis.replace(/_/g, ' ')}</Badge>}
                      </BadgeRow>
                    )}
                    {e.quote?.text && (
                      <Quote color={TRINITY_COLORS.enriching}>
                        "{e.quote.text.slice(0, 150)}
                        {e.quote.text.length > 150 ? '...' : ''}"
                        {e.quote.source && <QuoteSource> — {e.quote.source.split('/').pop()}</QuoteSource>}
                      </Quote>
                    )}
                    {e.brand_specific && e.brand_note && <ResearchNote>{e.brand_note}</ResearchNote>}
                  </InsightItem>
                ))}
              </InsightList>
            </EnrichingSection>
          )}

          {/* Tensions — amber */}
          {tensions.length > 0 && (
            <TensionsSection>
              <SectionHeaderWithButton>
                <SectionHeaderLeft>
                  <Dot color={TRINITY_COLORS.tension} />
                  <SectionTitle color={TRINITY_COLORS.tension}>Tensions</SectionTitle>
                  <SectionSubtitle>Claims and interviews diverge — highest-value insight</SectionSubtitle>
                </SectionHeaderLeft>
                {tensions.length > 2 && (
                  <ExpandButton onClick={() => setTensionExpanded((t) => !t)}>
                    {tensionExpanded ? '▲ Less' : `▼ All ${tensions.length}`}
                  </ExpandButton>
                )}
              </SectionHeaderWithButton>
              <TensionList>
                {(tensionExpanded ? tensions : tensions.slice(0, 2)).map((t, i) => (
                  <TensionItem key={i} type={t.type}>
                    <TensionHeader>
                      <TensionLabel type={t.type}>{t.label}</TensionLabel>
                      <SeverityBadge severity={t.severity}>{t.severity}</SeverityBadge>
                    </TensionHeader>
                    <TensionGrid>
                      <TensionColumn>
                        <TensionColumnLabel>Claims show</TensionColumnLabel>
                        <TensionText>{t.claims_signal}</TensionText>
                      </TensionColumn>
                      <TensionColumn>
                        <TensionColumnLabel contrast>Interviews reveal</TensionColumnLabel>
                        <TensionText>{t.interview_signal}</TensionText>
                      </TensionColumn>
                    </TensionGrid>
                    {t.recommendation && (
                      <RecommendationText>
                        <strong>Action: </strong>
                        {t.recommendation}
                      </RecommendationText>
                    )}
                    {t.research_note && <ResearchNote>{t.research_note}</ResearchNote>}
                  </TensionItem>
                ))}
              </TensionList>
            </TensionsSection>
          )}

          {/* Barriers & Drivers */}
          {insight.observation_enrichment && (() => {
            const oe = insight.observation_enrichment;
            const barriers = oe.barriers || [];
            const drivers = oe.drivers || [];
            if (barriers.length === 0 && drivers.length === 0) return null;
            return (
              <ObservationSection>
                <SectionHeader>
                  <Dot color={TRINITY_COLORS.neutral} />
                  <SectionTitle color="#323232">Barriers & Drivers</SectionTitle>
                  <SectionSubtitle>
                    {oe.n_observations} observations from {oe.n_respondents} respondents
                  </SectionSubtitle>
                </SectionHeader>
                <ObservationGrid>
                  {barriers.length > 0 && (
                    <ObservationColumn>
                      <ObservationColumnTitle color={TRINITY_COLORS.barrier}>
                        Barriers ({barriers.length})
                      </ObservationColumnTitle>
                      <ObservationList>
                        {barriers.slice(0, 4).map((b, i) => (
                          <ObservationItem key={i} borderColor={TRINITY_COLORS.barrier}>
                            <ObservationText>
                              {b.summary?.slice(0, 100)}
                              {b.summary && b.summary.length > 100 ? '...' : ''}
                            </ObservationText>
                            <BadgeRow>
                              {b.brand_id && <Badge color="blue">{b.brand_id}</Badge>}
                              {b.evidence_basis && <Badge color="blue">{b.evidence_basis.replace(/_/g, ' ')}</Badge>}
                              {b.intensity && <IntensityBadge intensity={b.intensity}>{b.intensity}</IntensityBadge>}
                            </BadgeRow>
                            {b.quote && (
                              <>
                                <ShowQuoteBtn onClick={() => setBarrierQuotesOpen(prev => ({ ...prev, [i]: !prev[i] }))}>
                                  {barrierQuotesOpen[i] ? '− Hide Quote' : '+ Show Quote'}
                                </ShowQuoteBtn>
                                {barrierQuotesOpen[i] && (
                                  <Quote color={TRINITY_COLORS.barrier}>
                                    "{b.quote}"
                                  </Quote>
                                )}
                              </>
                            )}
                          </ObservationItem>
                        ))}
                      </ObservationList>
                    </ObservationColumn>
                  )}
                  {drivers.length > 0 && (
                    <ObservationColumn>
                      <ObservationColumnTitle color={TRINITY_COLORS.driver}>Drivers ({drivers.length})</ObservationColumnTitle>
                      <ObservationList>
                        {drivers.slice(0, 4).map((d, i) => (
                          <ObservationItem key={i} borderColor={TRINITY_COLORS.driver}>
                            <ObservationText>
                              {d.summary?.slice(0, 100)}
                              {d.summary && d.summary.length > 100 ? '...' : ''}
                            </ObservationText>
                            <BadgeRow>
                              {d.brand_id && <Badge color="blue">{d.brand_id}</Badge>}
                              {d.evidence_basis && <Badge color="blue">{d.evidence_basis.replace(/_/g, ' ')}</Badge>}
                              {d.intensity && <IntensityBadge intensity={d.intensity}>{d.intensity}</IntensityBadge>}
                            </BadgeRow>
                            {d.quote && (
                              <>
                                <ShowQuoteBtn onClick={() => setDriverQuotesOpen(prev => ({ ...prev, [i]: !prev[i] }))}>
                                  {driverQuotesOpen[i] ? '− Hide Quote' : '+ Show Quote'}
                                </ShowQuoteBtn>
                                {driverQuotesOpen[i] && (
                                  <Quote color={TRINITY_COLORS.confirming}>
                                    "{d.quote}"
                                  </Quote>
                                )}
                              </>
                            )}
                          </ObservationItem>
                        ))}
                      </ObservationList>
                    </ObservationColumn>
                  )}
                </ObservationGrid>
              </ObservationSection>
            );
          })()}

          {/* Brand Intelligence */}
          {insight.observation_enrichment?.brand_insights && insight.observation_enrichment.brand_insights.length > 0 && (
            <BrandIntelligenceSection>
              <SectionHeader>
                <Dot color={TRINITY_COLORS.enriching} />
                <SectionTitle color={TRINITY_COLORS.enriching}>Brand Intelligence</SectionTitle>
              </SectionHeader>
              <BrandList>
                {insight.observation_enrichment.brand_insights.slice(0, 5).map((bi, i) => {
                  const total = bi.positive + bi.negative + (bi.neutral || 0) + (bi.mixed || 0);
                  const posPct = total > 0 ? Math.round((bi.positive / total) * 100) : 0;
                  const negPct = total > 0 ? Math.round((bi.negative / total) * 100) : 0;
                  return (
                    <BrandItem key={i} isFocal={bi.is_focal}>
                      <BrandHeader>
                        <BrandName isFocal={bi.is_focal}>{bi.brand_id}</BrandName>
                        {bi.is_focal && <FocalBadge>Focal</FocalBadge>}
                        <BrandStats>
                          <span style={{ color: '#8BC34A' }}>+{bi.positive}</span>
                          <span style={{ color: '#d32f2f' }}>-{bi.negative}</span>
                          <span style={{ color: '#323232' }}>({total} obs)</span>
                        </BrandStats>
                      </BrandHeader>
                      <SentimentBar>
                        <SentimentSegment color="#8BC34A" width={posPct} />
                        <SentimentSegment color="rgba(0, 0, 0, 0.12)" width={100 - posPct - negPct} />
                        <SentimentSegment color="#d32f2f" width={negPct} />
                      </SentimentBar>
                      <BadgeRow>
                        {Object.entries(bi.top_topics || {})
                          .slice(0, 3)
                          .map(([topic, count]) => (
                            <Badge key={topic} color="blue">
                              {topic.replace(/_/g, ' ')} ({count})
                            </Badge>
                          ))}
                      </BadgeRow>
                      {bi.top_barriers && bi.top_barriers.length > 0 && (
                        <BrandNote color={TRINITY_COLORS.barrier}>
                          Barrier: {bi.top_barriers[0]?.slice(0, 80)}
                          {bi.top_barriers[0] && bi.top_barriers[0].length > 80 ? '...' : ''}
                        </BrandNote>
                      )}
                      {bi.top_drivers && bi.top_drivers.length > 0 && (
                        <BrandNote color={TRINITY_COLORS.driver}>
                          Driver: {bi.top_drivers[0]?.slice(0, 80)}
                          {bi.top_drivers[0] && bi.top_drivers[0].length > 80 ? '...' : ''}
                        </BrandNote>
                      )}
                    </BrandItem>
                  );
                })}
              </BrandList>
            </BrandIntelligenceSection>
          )}

          {/* Messaging Hooks */}
          {insight.observation_enrichment?.messaging_quotes && insight.observation_enrichment.messaging_quotes.length > 0 && (
            <MessagingSection>
              <SectionHeader>
                <Dot color={TRINITY_COLORS.enriching} />
                <SectionTitle color={TRINITY_COLORS.enriching}>Messaging Hooks</SectionTitle>
                <SectionSubtitle>Observation-backed talking points</SectionSubtitle>
              </SectionHeader>
              <MessagingList>
                {insight.observation_enrichment.messaging_quotes.slice(0, 4).map((mq, i) => (
                  <MessagingItem key={i}>
                    <Quote color="#00bcd4">
                      "{mq.quote?.slice(0, 120)}
                      {mq.quote && mq.quote.length > 120 ? '...' : ''}"
                    </Quote>
                    <BadgeRow>
                      {mq.brand_id && <Badge color="purple">{mq.brand_id}</Badge>}
                      {mq.content_topic && <Badge color="blue">{mq.content_topic.replace(/_/g, ' ')}</Badge>}
                      {mq.evidence_basis && <Badge color="blue">{mq.evidence_basis.replace(/_/g, ' ')}</Badge>}
                    </BadgeRow>
                  </MessagingItem>
                ))}
              </MessagingList>
            </MessagingSection>
          )}
        </Content>
      )}
    </Root>
  );
}

// Styled Components

const Root = styled.div`
  margin-bottom: 16px;
  font-family: 'HarmoniaSans W01', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #323232;
`;

const HeaderButton = styled.button`
  width: 100%;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 12px 16px;
  text-align: left;
  cursor: pointer;
  transition: background 120ms ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
`;

const HeaderTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TripleConvergenceBadge = styled.span`
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
  background: #f5f5f5;
  color: #626262;
  border: 1px solid #e0e0e0;
`;

const SummaryPill = styled.span<{ color: string }>`
  font-size: 14px;
  padding: 2px 6px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.color}40;
  background: ${(p) => p.color}15;
  color: ${(p) => p.color};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PersonaMatchSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PersonaLabel = styled.span`
  font-size: 14px;
  color: #1976D2;
`;

const ProgressBar = styled.div`
  width: 40px;
  height: 6px;
  background: rgba(0, 0, 0, 0.12);
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #1976D2;
  border-radius: 999px;
`;

const ScoreText = styled.span`
  font-size: 14px;
  color: #1976D2;
  font-family: 'Courier New', monospace;
`;

const QualityBadge = styled.span<{ quality: string }>`
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
  background: #f5f5f5;
  color: #626262;
  border: 1px solid #e0e0e0;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: #323232;
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(p) => (p.$expanded ? '180deg' : '0deg')});
  display: flex;
  align-items: center;
`;

const Content = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NoCoverageCard = styled.div`
  background: #f5f5f5;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 12px 16px;
`;

const NoCoverageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const Dot = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.color};
  flex-shrink: 0;
`;

const NoCoverageTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #323232;
`;

const InfoIconStyled = styled(InfoIcon)`
  color: #323232;
`;

const NoCoverageText = styled.p`
  font-size: 14px;
  color: #323232;
  margin: 0;
`;

const ModeFramingCard = styled.div`
  background: #f5f5f5;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 12px 16px;
`;

const ModeText = styled.p`
  font-size: 14px;
  color: #323232;
  line-height: 1.4;
  margin: 0 0 6px 0;
`;

const MatchDetailToggle = styled.button`
  font-size: 14px;
  color: #8BC34A;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const MatchDetail = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`;

const MatchDescription = styled.p`
  font-size: 14px;
  color: #323232;
  margin: 0;
`;

const ConfirmingSection = styled.div`
  background: #ffffff;
  border: 1px solid #B3E0F5;
  border-radius: 12px;
  padding: 12px 16px;
`;

const EnrichingSection = styled.div`
  background: #ffffff;
  border: 1px solid #B3E0F5;
  border-radius: 12px;
  padding: 12px 16px;
`;

const TensionsSection = styled.div`
  background: #ffffff;
  border: 1px solid #B3E0F5;
  border-radius: 12px;
  padding: 12px 16px;
`;

const ObservationSection = styled.div`
  background: #ffffff;
  border: 1px solid #B3E0F5;
  border-radius: 12px;
  padding: 12px 16px;
`;

const BrandIntelligenceSection = styled.div`
  background: #ffffff;
  border: 1px solid #B3E0F5;
  border-radius: 12px;
  padding: 12px 16px;
`;

const MessagingSection = styled.div`
  background: #ffffff;
  border: 1px solid #B3E0F5;
  border-radius: 12px;
  padding: 12px 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const SectionHeaderWithButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SectionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionTitle = styled.span<{ color: string }>`
  font-size: 14px;
  font-weight: 600;
  color: #1976D2;
`;

const SectionSubtitle = styled.span`
  font-size: 14px;
  color: #323232;
`;

const ExpandButton = styled.button`
  font-size: 14px;
  color: #323232;
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #323232;
  }
`;

const SignalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SignalItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SignalText = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #323232;
  margin: 0;
`;

const EvidenceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const EvidenceItem = styled.div`
  font-size: 14px;
  color: #323232;
`;

const EvidenceLabel = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: #323232;
  opacity: 0.7;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Badge = styled.span<{ color: 'blue' | 'green' }>`
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
  background: #f5f5f5;
  color: #626262;
  border: 1px solid #e0e0e0;
`;

const IntensityBadge = styled.span<{ intensity: string }>`
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
  background: #f5f5f5;
  color: #626262;
  border: 1px solid #e0e0e0;
`;

const Quote = styled.p<{ color: string }>`
  font-size: 14px;
  font-style: italic;
  color: #1976D2;
  opacity: 0.9;
  border-left: 2px solid #1976D240;
  padding-left: 8px;
  margin: 4px 0 0 0;
`;

const QuoteSource = styled.span`
  font-style: normal;
  color: #323232;
`;

const ResearchNote = styled.p`
  font-size: 14px;
  color: #3f51b5;
  opacity: 0.7;
  margin: 4px 0 0 0;
`;

const InsightList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InsightItem = styled.div`
  border-left: 2px solid rgba(25, 118, 210, 0.3);
  padding-left: 10px;
`;

const InsightText = styled.p`
  font-size: 14px;
  color: #323232;
  line-height: 1.4;
  margin: 0 0 6px 0;
`;

const TensionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TensionItem = styled.div<{ type: string }>`
  background: #ffffff;
  border: 1px solid #B3E0F5;
  border-radius: 8px;
  padding: 10px 12px;
`;

const TensionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const TensionLabel = styled.span<{ type: string }>`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
`;

const SeverityBadge = styled.span<{ severity: string }>`
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
  background: #f5f5f5;
  color: #626262;
  border: 1px solid #e0e0e0;
`;

const TensionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 8px;
`;

const TensionColumn = styled.div``;

const TensionColumnLabel = styled.div<{ contrast?: boolean }>`
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: #1976D2;
  opacity: 0.9;
  margin-bottom: 4px;
`;

const TensionText = styled.p`
  font-size: 14px;
  color: #323232;
  line-height: 1.4;
  margin: 0;
`;

const RecommendationText = styled.p`
  font-size: 14px;
  color: #323232;
  line-height: 1.4;
  margin: 4px 0 0 0;

  strong {
    color: #323232;
  }
`;

const ObservationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const ObservationColumn = styled.div``;

const ObservationColumnTitle = styled.div<{ color: string }>`
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: #1976D2;
  opacity: 0.9;
  margin-bottom: 8px;
`;

const ObservationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ObservationItem = styled.div<{ borderColor: string }>`
  border-left: 2px solid ${(p) => p.borderColor}40;
  padding-left: 8px;
`;

const ObservationText = styled.p`
  font-size: 14px;
  color: #323232;
  line-height: 1.4;
  margin: 0 0 4px 0;
`;

const ShowQuoteBtn = styled.button`
  background: none;
  border: none;
  color: #1976D2;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
  margin-top: 4px;
  font-family: inherit;
  text-align: left;
  &:hover {
    text-decoration: underline;
  }
`;

const BrandList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BrandItem = styled.div<{ isFocal: boolean }>`
  background: ${(p) => (p.isFocal ? 'rgba(139, 195, 74, 0.08)' : '#ffffff')};
  border: ${(p) => (p.isFocal ? '1px solid rgba(139, 195, 74, 0.3)' : '1px solid #e0e0e0')};
  border-radius: 8px;
  padding: 10px 12px;
`;

const BrandHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const BrandName = styled.span<{ isFocal: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => (p.isFocal ? '#1976D2' : '#323232')};
`;

const FocalBadge = styled.span`
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
  background: #f5f5f5;
  color: #626262;
  border: 1px solid #e0e0e0;
  margin-left: auto;
  margin-right: 8px;
`;

const BrandStats = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
`;

const SentimentBar = styled.div`
  display: flex;
  height: 6px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.06);
  margin-bottom: 6px;
`;

const SentimentSegment = styled.div<{ color: string; width: number }>`
  background: ${(p) => p.color};
  width: ${(p) => p.width}%;
  height: 100%;
`;

const BrandNote = styled.p<{ color: string }>`
  font-size: 14px;
  color: #323232;
  opacity: 0.9;
  line-height: 1.4;
  margin: 4px 0 0 0;
`;

const MessagingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MessagingItem = styled.div`
  border-left: 2px solid rgba(0, 188, 212, 0.3);
  padding-left: 10px;
`;
